/**
 * ABET Monitor Dashboard — Sidebar navigation with multiple sections
 * Deployed on Vercel for access from anywhere.
 *
 * Sections:
 *   1. MLB Monitors — HF Space health, Discord feeds, API keys
 *   2. TheOddsAPI — Multi-sport odds browser (8 tabs, built from HTML dashboard)
 *   3. Sportsbook Jobs — Job listings scraped from sportsbook career pages
 */

import { useState, useEffect, useCallback } from 'react'
import ApiQuotaBar from './widgets/ApiQuotaBar'
import { Menu, X, Monitor, TrendingUp, Briefcase, ClipboardList, BarChart3, ChevronRight, Server, Home, BookOpen, Database, Target, Settings } from 'lucide-react'
import { AuthProvider, UserMenu } from './auth'
import SocialsDropdown from './components/SocialsDropdown'
import HomePage from './components/HomePage'
import EventIntelligencePage from './components/EventIntelligencePage'
import {
  SpaceStatusWidget,
  MonitorHealthWidget,
  EventTimelineWidget,
  ApiKeyWidget,
  OddsStatusWidget,
  DiscordFeedWidget,
  MLBLeadersWidget,
  StatsMonitorWidget,
  SportsBrowserWidget,
  ParticipantsWidget,
  RawDataWidget,
  LiveScoresWidget,
  EventsBrowserWidget,
  EventOddsWidget,
  HistoricalOddsWidget,
  OddsComparisonWidget,
  JobsBrowserWidget,
  ScraperHealthWidget,
  ScraperCoverageWidget,
  LiveOddsTableWidget,
  LiveOddsVNextWidget,
  PropsBoardWidget,
  ArbitrageWidget,
  MiddlesWidget,
  LineMovementWidget,
  BookMatrixWidget,
  OracleVMHealthWidget,
  ExpansionCoverageWidget,
  PipelineStorageWidget,
  FleetLatencyWidget,
  MLBPredictionsWidget,
  LineupsWidget,
  PitchersWidget,
  UmpiresWidget,
  OddsChartsWidget,
  GameLinesModule,
  PositiveEVWidget,
  SystemHealthWidget,
  MetricsChartsWidget,
  ResourceUsageWidget,
  AlertHistoryWidget,
  JoinValidationProgressWidget,
  RawAccountingProofWidget,
  MasterScraperProofTrackerWidget,
  InfrastructureWidget,
  ITTicketsWidget,
} from './widgets'
import ScheduleWidget from './widgets/ScheduleWidget'
import MLBScheduleWidget from './widgets/MLBScheduleWidget'
import StatsEmbedWidget from './widgets/StatsEmbedWidget'
import ResearchWidget from './widgets/ResearchWidget'
import StrategiesWidget from './widgets/StrategiesWidget'
import {
  readEventTarget,
  resolveDashboardRoute,
  writeDashboardRoute,
  writeEventDashboardRoute,
} from './lib/appNavigation'
import { classifyBoardRelease, fetchBoardReleaseManifest } from './lib/boardRelease'
import { scheduleEventToTarget } from './lib/eventIntelligence'
import './App.css'

// ---------------------------------------------------------------------------
// Navigation config — each section has its own tabs
// ---------------------------------------------------------------------------
const SECTIONS = {
  home: {
    id: 'home',
    label: 'Home',
    icon: Home,
    description: 'Dashboard overview and quick navigation',
    tabs: [
      { id: 'landing', label: 'Home', icon: '🏠' },
    ],
  },
  event: {
    id: 'event',
    label: 'Event Intelligence',
    icon: Target,
    description: 'Every captured price, movement, edge, and source proof for one event',
    tabs: [
      { id: 'overview', label: 'Event Overview', icon: '🎯' },
    ],
  },
  mlb: {
    id: 'mlb',
    label: 'MLB Monitors',
    icon: Monitor,
    description: 'HF Space monitors, Discord feeds, API keys',
    tabs: [
      { id: 'overview', label: 'Overview', icon: '\u{1F4CA}' },
      { id: 'schedule', label: 'Schedule', icon: '\u{1F4C5}' },
      { id: 'lineups', label: 'Lineups', icon: '\u26BE' },
      { id: 'pitchers', label: 'Pitchers', icon: '\u{1F3AF}' },
      { id: 'umpires', label: 'Umpires', icon: '\u2696\uFE0F' },
      { id: 'odds', label: 'TheOddsAPI Odds', icon: '\u{1F4C8}' },
      { id: 'leaders', label: 'Leaders', icon: '\u{1F3C6}' },
      { id: 'events', label: 'Events', icon: '\u{1F527}' },
      { id: 'predictions', label: 'Predictions', icon: '\u{1F3AF}' },
    ],
  },
  theodds: {
    id: 'theodds',
    label: 'TheOddsAPI',
    icon: TrendingUp,
    description: 'Multi-sport odds browser — all endpoints',
    tabs: [
      { id: 'sports-browser', label: 'Sports', icon: '\u{1F3C6}' },
      { id: 'events-browser', label: 'Events', icon: '\u{1F4CB}' },
      { id: 'odds-comparison', label: 'Odds', icon: '\u{1F4B0}' },
      { id: 'event-odds', label: 'Event Odds', icon: '\u{1F50D}' },
      { id: 'live-scores', label: 'Live Scores', icon: '\u{1F534}' },
      { id: 'participants', label: 'Participants', icon: '\u{1F465}' },
      { id: 'historical', label: 'Historical', icon: '\u{1F4DA}' },
      { id: 'raw-data', label: 'Raw Data', icon: '\u{1F4BE}' },
    ],
  },
  jobs: {
    id: 'jobs',
    label: 'Sportsbook Jobs',
    icon: Briefcase,
    description: 'Job listings from DraftKings + more sportsbooks',
    tabs: [
      { id: 'jobs-browser', label: 'All Jobs', icon: '💼' },
    ],
  },
  liveodds: {
    id: 'liveodds',
    label: 'Live Odds',
    icon: BarChart3,
    description: 'Validated cross-book odds and market intelligence',
    tabs: [
      { id: 'odds-vnext', label: 'Odds Screen', icon: '📈' },
      { id: 'odds-table', label: 'Compact Grid', icon: '📊' },
      { id: 'positive-ev', label: '+EV', icon: '⚡' },
      { id: 'props-board', label: 'Props Board', icon: '🎯' },
      { id: 'arbitrage', label: 'Arbitrage', icon: '💰' },
      { id: 'middles', label: 'Middles', icon: '🎯' },
      { id: 'line-movement', label: 'Line Movement', icon: '📈' },
      { id: 'book-matrix', label: 'Book Matrix', icon: '🏛️' },
      { id: 'scraper-health', label: 'Scrapers', icon: '🔧' },
    ],
  },
  tickets: {
    id: 'tickets',
    label: 'IT Tickets',
    icon: ClipboardList,
    description: 'Incident, backlog, and certification tracking',
    tabs: [
      { id: 'it-tickets', label: 'IT Tickets', icon: '🎫' },
    ],
  },
  infrastructure: {
    id: 'infrastructure',
    label: 'Operations',
    icon: Settings,
    description: 'Infrastructure, pipeline, and project management',
    tabs: [
      { id: 'monitoring', label: 'Monitoring', icon: '📡' },
      { id: 'oracle-vm', label: 'Oracle VM', icon: '🖥️' },
      { id: 'fleet-latency', label: 'Fleet', icon: '⏱️' },
      { id: 'pipeline-storage', label: 'Pipeline', icon: '🗃️' },
      { id: 'expansion-coverage', label: 'Expansion', icon: '🧩' },
      { id: 'infra-status', label: 'Infrastructure', icon: '🏗️' },
    ],
  },
  research: {
    id: 'research',
    label: 'Research',
    icon: BookOpen,
    description: 'Predictive models, analytics sources, and methodology',
    tabs: [
      { id: 'models', label: 'Models', icon: '🧠' },
      { id: 'backtests', label: 'Backtests', icon: '📊' },
      { id: 'sources', label: 'Sources', icon: '📚' },
      { id: 'sabr', label: 'SABR 2026', icon: '⚾' },
    ],
  },
  stats: {
    id: 'stats',
    label: 'Stats',
    icon: Database,
    description: 'Multi-sport data sources and player/team analytics',
    tabs: [
      { id: 'mlb-stats', label: 'MLB', icon: '⚾', logoPath: '/logos/leagues/MLB.png' },
      { id: 'nba-stats', label: 'NBA', icon: '🏀', logoPath: '/logos/leagues/NBA.png' },
      { id: 'nfl-stats', label: 'NFL', icon: '🏈', logoPath: '/logos/leagues/NFL.svg' },
      { id: 'nhl-stats', label: 'NHL', icon: '🏒', logoPath: '/logos/leagues/NHL.png' },
      { id: 'cfb-stats', label: 'CFB', icon: '🎓', logoPath: '/logos/leagues/CFB.svg' },
      { id: 'cbb-stats', label: 'CBB', icon: '🏫', logoPath: '/logos/leagues/CBB.svg' },
      { id: 'soccer-stats', label: 'Soccer', icon: '⚽', logoPath: '/logos/leagues/SOCCER.svg' },
    ],
  },
  strategies: {
    id: 'strategies',
    label: 'Strategies',
    icon: Target,
    description: 'Strategy tracking, performance metrics, and portfolio optimization',
    tabs: [
      { id: 'strategy-tracker', label: 'Strategies', icon: '🎯' },
      { id: 'performance', label: 'Performance', icon: '📈' },
      { id: 'efficient-frontier', label: 'Frontier', icon: '📉' },
      { id: 'screener', label: 'Screener', icon: '🔍' },
    ],
  },
}

const SECTION_ORDER = ['home', 'liveodds', 'mlb', 'theodds', 'stats', 'research', 'strategies', 'tickets', 'infrastructure', 'jobs']

// ---------------------------------------------------------------------------
// Placeholder for TheOddsAPI tabs (until Steps 4-8 build real widgets)
// ---------------------------------------------------------------------------
function ComingSoonPlaceholder({ tabLabel, tabIcon, section }) {
  return (
    <div className="app-coming-soon">
      <div className="app-coming-soon-icon">{tabIcon}</div>
      <div className="app-coming-soon-title">{tabLabel}</div>
      <div className="app-coming-soon-section">{section}</div>
      <div className="app-coming-soon-msg">
        This widget is under construction. It will be built from the existing
        TheOddsAPI HTML dashboard and routed through the CF Worker proxy.
      </div>
      <div className="app-coming-soon-badge">COMING SOON</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
function App() {
  const initialRoute = resolveDashboardRoute(SECTIONS, window.location.search)
  const [activeSection, setActiveSection] = useState(initialRoute.sectionId)
  const [activeTab, setActiveTab] = useState(initialRoute.tabId)
  const [eventTarget, setEventTarget] = useState(() => readEventTarget(window.location.search))
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [boardRelease, setBoardRelease] = useState({ state: 'checking', capturedAt: null, ageMs: null })
  const [quickOddsSport, setQuickOddsSport] = useState(null)
  const [quickEventsSport, setQuickEventsSport] = useState(null)
  const [quickEventOddsTarget, setQuickEventOddsTarget] = useState({ sport: null, eventId: null })
  const [oddsQuota, setOddsQuota] = useState({ remaining: null, used: null, lastUpdated: null })

  const updateOddsQuota = useCallback((remaining, used) => {
    setOddsQuota({ remaining, used, lastUpdated: new Date() })
  }, [])

  useEffect(() => {
    let cancelled = false
    const refreshRelease = async () => {
      try {
        const manifest = await fetchBoardReleaseManifest()
        if (!cancelled) setBoardRelease(classifyBoardRelease(manifest))
      } catch {
        if (!cancelled) setBoardRelease({ state: 'unavailable', capturedAt: null, ageMs: null })
      }
    }
    refreshRelease()
    const interval = setInterval(refreshRelease, 30_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    writeDashboardRoute(activeSection, activeTab, { replace: true })
    const handlePopState = () => {
      const route = resolveDashboardRoute(SECTIONS, window.location.search)
      setActiveSection(route.sectionId)
      setActiveTab(route.tabId)
      setEventTarget(readEventTarget(window.location.search))
      setSidebarOpen(false)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Close sidebar on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const navigateTo = useCallback((sectionId, tabId) => {
    const section = SECTIONS[sectionId] || SECTIONS.home
    const tab = section.tabs.find((entry) => entry.id === tabId) || section.tabs[0]
    setActiveSection(section.id)
    setActiveTab(tab.id)
    if (section.id !== 'event') setEventTarget(null)
    writeDashboardRoute(section.id, tab.id)
    setSidebarOpen(false)
  }, [])

  const openEvent = useCallback((event, sportKey) => {
    const target = scheduleEventToTarget(event, sportKey)
    if (!target.awayTeam || !target.homeTeam) return
    setEventTarget(target)
    setActiveSection('event')
    setActiveTab('overview')
    writeEventDashboardRoute(target)
    setSidebarOpen(false)
  }, [])

  const section = SECTIONS[activeSection]
  const tabs = section?.tabs || []
  const currentTab = tabs.find(t => t.id === activeTab)

  useEffect(() => {
    document.title = activeSection === 'event' && eventTarget
      ? `${eventTarget.awayTeam} @ ${eventTarget.homeTeam} · ABET`
      : `${currentTab?.label || section.label} · ${section.label} · ABET`
  }, [activeSection, currentTab?.label, eventTarget, section.label])

  return (
    <AuthProvider>
    <div className="app">
      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-brand">ABET</span>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {SECTION_ORDER.map(sKey => {
            const s = SECTIONS[sKey]
            const SIcon = s.icon
            const isActive = activeSection === sKey

            return (
              <div key={sKey} className={`sidebar-section ${isActive ? 'active' : ''}`}>
                <button
                  className="sidebar-section-btn"
                  onClick={() => navigateTo(sKey, s.tabs[0].id)}
                >
                  <SIcon size={18} />
                  <div className="sidebar-section-text">
                    <span className="sidebar-section-label">{s.label}</span>
                    <span className="sidebar-section-desc">{s.description}</span>
                  </div>
                  <ChevronRight size={14} className="sidebar-section-arrow" />
                </button>

                {isActive && (
                  <div className="sidebar-tab-list">
                    {s.tabs.map(tab => (
                      <button
                        key={tab.id}
                        className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => navigateTo(sKey, tab.id)}
                      >
                        {tab.logoPath && (
                          <span className="sidebar-tab-icon">
                            <img src={tab.logoPath} alt={tab.label} className="sidebar-tab-logo" />
                          </span>
                        )}
                        <span className="sidebar-tab-label">{tab.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <span>v2.0 — Modular Dashboard</span>
        </div>
      </aside>

      {/* Header */}
      <header className="app-header">
        <div className="app-header-left">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          {/* LOGO LINK 2026-08-10: the wordmark was inert. It now returns to the
              homepage, which is what every user expects of a masthead logo. */}
          <button
            type="button"
            className="app-logo"
            aria-label="ABET — go to homepage"
            title="Go to homepage"
            onClick={() => navigateTo('home')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <span className="app-logo-alpha">&alpha;</span>
            <span className="app-logo-wordmark">bet</span>
          </button>
          <h1 className="app-title">{section.label}</h1>
        </div>
        <div className="app-header-right">
          {/* SCOPE FIX 2026-08-10: this chip reports the ODDS BOARD release manifest, but it
              renders on every section — so on the Jobs tab it claimed "CURRENT" about data
              that has nothing to do with jobs. It is now labelled with what it measures. */}
          <span className={`app-live-dot ${boardRelease.state} ${boardRelease.state === 'current' ? 'pulse' : ''}`} title="Odds board release freshness" />
          <span className={`app-live-text ${boardRelease.state}`} title="Odds board release freshness">
            {boardRelease.state === 'current' ? 'ODDS CURRENT' : boardRelease.state === 'stale' ? 'STALE DATA' : boardRelease.state === 'checking' ? 'CHECKING' : 'DATA OFFLINE'}
          </span>
          <span className="app-last-refresh">
            {boardRelease.capturedAt ? new Date(boardRelease.capturedAt).toLocaleTimeString() : '—'}
          </span>
          <SocialsDropdown />
          <UserMenu />
        </div>
      </header>

      {/* Tab bar (for active section) */}
      {activeSection !== 'home' && activeSection !== 'event' && (
        <nav className="app-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`app-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => navigateTo(activeSection, tab.id)}
            >
              {tab.logoPath && (
                <span className="app-tab-icon">
                  <img src={tab.logoPath} alt={tab.label} className="app-tab-logo" />
                </span>
              )}
              <span className="app-tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Content */}
      <main className="app-content">
        {/* ============ HOME SECTION ============ */}
        {activeSection === 'home' && (
          <HomePage navigateTo={navigateTo} openEvent={openEvent} sections={SECTIONS} sectionOrder={SECTION_ORDER} />
        )}

        {activeSection === 'event' && (
          <EventIntelligencePage
            target={eventTarget}
            navigateTo={navigateTo}
            onBack={() => navigateTo('home', 'landing')}
          />
        )}

        {/* ============ MLB MONITORS SECTION ============ */}
        {activeSection === 'mlb' && (
          <>
            {activeTab === 'overview' && (
              <>
                <div className="app-grid-row app-grid-row-2">
                  <SpaceStatusWidget />
                  <MonitorHealthWidget />
                </div>
                <div className="app-grid-row app-grid-row-1">
                  <StatsMonitorWidget sportFilter="mlb" />
                </div>
                <div className="app-grid-row app-grid-row-1">
                  <DiscordFeedWidget showFilterBar={true} />
                </div>
              </>
            )}

            {activeTab === 'schedule' && (
              <div className="app-grid-row app-grid-row-1">
                <MLBScheduleWidget />
              </div>
            )}

            {activeTab === 'lineups' && (
              <div className="app-grid-row app-grid-row-1">
                <LineupsWidget />
              </div>
            )}

            {activeTab === 'pitchers' && (
              <div className="app-grid-row app-grid-row-1">
                <PitchersWidget />
              </div>
            )}

            {activeTab === 'umpires' && (
              <div className="app-grid-row app-grid-row-1">
                <UmpiresWidget />
              </div>
            )}

            {activeTab === 'odds' && (
              <>
                <div className="app-grid-row app-grid-row-1">
                  <GameLinesModule />
                </div>
                <div className="app-grid-row app-grid-row-1">
                  <OddsChartsWidget />
                </div>
              </>
            )}

            {activeTab === 'leaders' && (
              <div className="app-grid-row app-grid-row-1">
                <MLBLeadersWidget />
              </div>
            )}

            {activeTab === 'events' && (
              <div className="app-grid-row app-grid-row-1">
                <EventTimelineWidget />
              </div>
            )}

            {activeTab === 'predictions' && (
              <div className="app-grid-row app-grid-row-1">
                <MLBPredictionsWidget />
              </div>
            )}
          </>
        )}

        {/* ============ THEODDSAPI SECTION ============ */}
        {activeSection === 'theodds' && (
          <>
            <ApiQuotaBar
              remaining={oddsQuota.remaining}
              used={oddsQuota.used}
              lastUpdated={oddsQuota.lastUpdated}
            />

            {activeTab === 'sports-browser' && (
              <div className="app-grid-row app-grid-row-1">
                <SportsBrowserWidget
                  onQuickOdds={(sportKey) => {
                    setQuickOddsSport(sportKey)
                    navigateTo('theodds', 'odds-comparison')
                  }}
                  onQuickEvents={(sportKey) => {
                    setQuickEventsSport(sportKey)
                    navigateTo('theodds', 'events-browser')
                  }}
                  onQuotaUpdate={updateOddsQuota}
                />
              </div>
            )}

            {activeTab === 'events-browser' && (
              <div className="app-grid-row app-grid-row-1">
                <EventsBrowserWidget
                  initialSport={quickEventsSport}
                  onGetEventOdds={(sportKey, eventId) => {
                    setQuickEventOddsTarget({ sport: sportKey, eventId })
                    navigateTo('theodds', 'event-odds')
                  }}
                />
              </div>
            )}

            {activeTab === 'odds-comparison' && (
              <div className="app-grid-row app-grid-row-1">
                <OddsComparisonWidget initialSport={quickOddsSport} onQuotaUpdate={updateOddsQuota} />
              </div>
            )}

            {activeTab === 'live-scores' && (
              <div className="app-grid-row app-grid-row-1">
                <LiveScoresWidget onQuotaUpdate={updateOddsQuota} />
              </div>
            )}

            {activeTab === 'event-odds' && (
              <div className="app-grid-row app-grid-row-1">
                <EventOddsWidget
                  initialSport={quickEventOddsTarget.sport}
                  initialEventId={quickEventOddsTarget.eventId}
                  onQuotaUpdate={updateOddsQuota}
                />
              </div>
            )}

            {activeTab === 'historical' && (
              <div className="app-grid-row app-grid-row-1">
                <HistoricalOddsWidget onQuotaUpdate={updateOddsQuota} />
              </div>
            )}

            {activeTab === 'participants' && (
              <div className="app-grid-row app-grid-row-1">
                <ParticipantsWidget onQuotaUpdate={updateOddsQuota} />
              </div>
            )}

            {activeTab === 'raw-data' && (
              <div className="app-grid-row app-grid-row-1">
                <RawDataWidget />
              </div>
            )}

            {!['sports-browser', 'events-browser', 'odds-comparison', 'event-odds', 'live-scores', 'participants', 'historical', 'raw-data'].includes(activeTab) && (
              <ComingSoonPlaceholder
                tabLabel={currentTab?.label || 'Unknown'}
                tabIcon={currentTab?.icon || '\u{2753}'}
                section="TheOddsAPI Dashboard"
              />
            )}
          </>
        )}

        {/* ============ SPORTSBOOK JOBS SECTION ============ */}
        {activeSection === 'jobs' && (
          <>
            {activeTab === 'jobs-browser' && (
              <div className="app-grid-row app-grid-row-1">
                <JobsBrowserWidget />
              </div>
            )}
          </>
        )}

        {/* ============ LIVE ODDS SECTION ============ */}
        {activeSection === 'liveodds' && (
          <>
            {activeTab === 'odds-table' && (
              <div className="app-grid-row app-grid-row-1">
                <LiveOddsTableWidget />
              </div>
            )}

            {activeTab === 'odds-vnext' && (
              <div className="app-grid-row app-grid-row-1">
                <LiveOddsVNextWidget />
              </div>
            )}

            {activeTab === 'positive-ev' && (
              <div className="app-grid-row app-grid-row-1">
                <PositiveEVWidget />
              </div>
            )}

            {activeTab === 'props-board' && (
              <div className="app-grid-row app-grid-row-1">
                <PropsBoardWidget />
              </div>
            )}

            {activeTab === 'scraper-health' && (
              <>
                <div className="app-grid-row app-grid-row-1">
                  <ScraperCoverageWidget />
                </div>
                <div className="app-grid-row app-grid-row-1">
                  <ScraperHealthWidget />
                </div>
              </>
            )}

            {activeTab === 'arbitrage' && (
              <div className="app-grid-row app-grid-row-1">
                <ArbitrageWidget />
              </div>
            )}

            {activeTab === 'middles' && (
              <div className="app-grid-row app-grid-row-1">
                <MiddlesWidget />
              </div>
            )}

            {activeTab === 'line-movement' && (
              <div className="app-grid-row app-grid-row-1">
                <LineMovementWidget />
              </div>
            )}

            {activeTab === 'book-matrix' && (
              <div className="app-grid-row app-grid-row-1">
                <BookMatrixWidget />
              </div>
            )}
          </>
        )}

        {activeSection === 'tickets' && (
          <div className="app-grid-row app-grid-row-1">
            <ITTicketsWidget />
          </div>
        )}

        {activeSection === 'infrastructure' && (
          <>
            {activeTab === 'monitoring' && (
              <>
                <MasterScraperProofTrackerWidget />
                <RawAccountingProofWidget />
                <JoinValidationProgressWidget />
                <SystemHealthWidget />
                <ResourceUsageWidget />
                <MetricsChartsWidget />
                <AlertHistoryWidget />
              </>
            )}
            {activeTab === 'oracle-vm' && (
              <div className="app-grid-row app-grid-row-1">
                <OracleVMHealthWidget />
              </div>
            )}
            {activeTab === 'fleet-latency' && (
              <div className="app-grid-row app-grid-row-1">
                <FleetLatencyWidget />
              </div>
            )}
            {activeTab === 'pipeline-storage' && (
              <div className="app-grid-row app-grid-row-1">
                <PipelineStorageWidget />
              </div>
            )}
            {activeTab === 'expansion-coverage' && (
              <div className="app-grid-row app-grid-row-1">
                <ExpansionCoverageWidget />
              </div>
            )}
            {activeTab === 'infra-status' && (
              <div className="app-grid-row app-grid-row-1">
                <InfrastructureWidget />
              </div>
            )}
          </>
        )}

        {/* ============ RESEARCH SECTION ============ */}
        {activeSection === 'research' && (
          <div className="app-grid-row app-grid-row-1">
            <ResearchWidget tab={activeTab} />
          </div>
        )}

        {/* ============ STATS SECTION ============ */}
        {activeSection === 'stats' && (
          <div className="app-grid-row app-grid-row-1">
            <StatsEmbedWidget sportTab={activeTab} />
          </div>
        )}

        {/* ============ STRATEGIES SECTION ============ */}
        {activeSection === 'strategies' && (
          <div className="app-grid-row app-grid-row-1">
            <StrategiesWidget tab={activeTab} />
          </div>
        )}
      </main>

      <footer className="app-footer">
        <span>ABET Monitor Suite</span>
        <span>|</span>
        <span>HuggingFace + Cloudflare Workers + Vercel</span>
        <span>|</span>
        <a href="/embed/all" target="_blank" rel="noopener">Embed Widgets</a>
      </footer>
    </div>
    </AuthProvider>
  )
}

export default App
